// @ts-strict-ignore
import * as React from 'react';

import { ArrowRightOutlined } from '@ant-design/icons';
import Button from 'antd/lib/button';
import Collapse from 'antd/lib/collapse';
import Space from 'antd/lib/space';
import Text from 'antd/lib/typography/Text';
import { antGrey, releaseStepDetailsContainerModuleStyle, runwayColors, tagStyle } from './styles/globalStyles';
import BetaWhatToTestModule from './betaModules/BetaWhatToTestModule';
import BetaGroupsModule from './betaModules/BetaGroupsModule';
import {
    BetaBuild,
    BetaStatus,
    CIBuildSelectionReason,
    BetaTestingGroup,
    BetaTester,
    TestTrack,
    IntegrationType,
    RunwayResponseType,
} from '../types';
import { getStabilityInfos, isGoogleBetaIntegration, isTestFlightBetaIntegration } from '../helpers';
import List from 'antd/lib/list';
import { colorForBetaBuildStatus, displayTextForBetaBuildStatus } from '../helpers/displayHelpers';
import Tag from 'antd/lib/tag';
import { PLAY_CONSOLE_URL } from '../constants';
import { useDispatch, useSelector } from 'react-redux';
import { ReleaseUpdatable } from '../actions/updatable';
import { PromoteToTestingTrackRequestReleaseStep, tryUpdateRelease } from '../actions/release';
import { RunwayUserAction } from '../helpers/aclHelpers';
import PermissionedActionButton from './HOCs/PermissionedActionButton';
import { Nullable, ReleaseComponentProps, StepRunComponentProps } from '../typescriptHelpers';
import Checkbox from 'antd/lib/checkbox';
import BetaBuildHistoryItemHeader from './betaTesting/BetaBuildHistoryItemHeader';
import { reduce } from 'lodash';
import { selectBetaIntegrationLinkUrl } from '../selectors/selectors';
import { RootState } from '../reducers/reducers';
import StabilityMetricsContainer from './selectedAppStoreBuild/StabilityMetricsContainer';
import Menu from 'antd/lib/menu';
import { globalMessage } from '../utils/globalMessage';
import { useCallUpdatable, useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { StepRunUpdatable } from '@/actions/stepRun';
import { PromoteBuildConfirmationModal } from './PromoteBuildConfirmationModal';

interface GoogleBetaBuildCtasProps extends BetaBuild, ReleaseComponentProps {
    testTracks: TestTrack[];
    defaultTestTrack: string;
    highestTestingBuildId: number;
    stepRunComponentProps?: Nullable<StepRunComponentProps>;
}

const GoogleBetaBuildCtas: React.FC<GoogleBetaBuildCtasProps> = (props: GoogleBetaBuildCtasProps) => {
    const promoteBuildToTestingTrackLoadingReleaseStep = useIsLoading(
        ReleaseUpdatable.PromoteBuildToTestingTrack,
        props.appStoreBuild.appStoreBuild.id,
    );
    const promoteBuildToTestingTrackSuccessReleaseStep = useUpdated(
        ReleaseUpdatable.PromoteBuildToTestingTrack,
        props.appStoreBuild.appStoreBuild.id,
    );
    const promoteBuildToTestingTrackErrorReleaseStep = useError(
        ReleaseUpdatable.PromoteBuildToTestingTrack,
        props.appStoreBuild.appStoreBuild.id,
    );

    const [
        promoteBuildsToTestingTrackStepRun,
        promoteBuildsToTestingTrackIsLoadingStepRun,
        promoteBuildsToTestingTrackResponseTypeStepRun,
        promoteBuildsToTestingTrackErrorStepRun,
    ] = useCallUpdatable(
        StepRunUpdatable.PromoteBuildsToTestingTrack,
        props.stepRunComponentProps,
        props.appStoreBuild?.appStoreBuild?.id,
    );

    const [pendingPromotionTrack, setPendingPromotionTrack] = React.useState<string | null>(null);

    const dispatch = useDispatch();

    const promoteBuildToTestingTrackLoading =
        props.stepRunComponentProps ?
            promoteBuildsToTestingTrackIsLoadingStepRun
        :   promoteBuildToTestingTrackLoadingReleaseStep;
    const promoteBuildToTestingTrackSuccess =
        props.stepRunComponentProps ?
            promoteBuildsToTestingTrackResponseTypeStepRun === RunwayResponseType.Success
        :   promoteBuildToTestingTrackSuccessReleaseStep;
    const promoteBuildToTestingTrackError =
        props.stepRunComponentProps ?
            promoteBuildsToTestingTrackErrorStepRun
        :   promoteBuildToTestingTrackErrorReleaseStep;

    React.useEffect(() => {
        if (promoteBuildToTestingTrackSuccess) {
            globalMessage.success('Build promoted to testing track!');

            setPendingPromotionTrack(null);
        }
    }, [promoteBuildToTestingTrackSuccess]);

    React.useEffect(() => {
        if (promoteBuildToTestingTrackError) {
            globalMessage.error(promoteBuildToTestingTrackError);
        }
    }, [promoteBuildToTestingTrackError]);

    const defaultTrack = props.defaultTestTrack ? props.defaultTestTrack : 'testing';
    const executeBuildPromotion = (buildId: string, track: string = null) => {
        const request: PromoteToTestingTrackRequestReleaseStep = {
            orgId: props.orgId,
            buildIds: [buildId],
            appId: props.appId,
            releaseId: props.releaseId,
            track,
        };

        if (props.stepRunComponentProps) {
            promoteBuildsToTestingTrackStepRun(request);
        } else {
            dispatch(tryUpdateRelease(ReleaseUpdatable.PromoteBuildToTestingTrack, request, buildId));
        }
    };

    const showPromotionModal = (track: string = null) => {
        setPendingPromotionTrack(track ?? defaultTrack);
    };

    const confirmPromotion = () => {
        executeBuildPromotion(
            props.appStoreBuild.appStoreBuild.id,
            pendingPromotionTrack !== defaultTrack ? pendingPromotionTrack : null,
        );
        setPendingPromotionTrack(null);
    };

    const cancelPromotion = () => {
        setPendingPromotionTrack(null);
    };

    // TODO: Full implementation should be handled in this ticket https://linear.app/runway-org/issue/RUN-11810
    const canPromoteBuild = true;
    // const canPromoteBuild =
    //     props.highestTestingBuildId ?
    //         parseInt(props.appStoreBuild.appStoreBuild.buildNumber) > props.highestTestingBuildId
    //     :   true;

    const testTrackOptionsMenu =
        props.testTracks && props.testTracks.length > 0 ?
            <Menu
                onClick={(item) => {
                    if (item.key) {
                        showPromotionModal(item.key);
                    }
                }}
                items={props.testTracks
                    .map((testTrack) => ({
                        label: `Promote to ${testTrack.name} track`,
                        key: testTrack.name,
                    }))
                    .filter((item) => item.key !== props.defaultTestTrack)}
            />
        :   null;

    return (
        <>
            <Space align="center">
                {canPromoteBuild && (
                    <PermissionedActionButton
                        releaseId={props.releaseId}
                        actionType={RunwayUserAction.UAPromoteBetaBuildToTestingTrack}
                        size="small"
                        onClick={() => showPromotionModal()}
                        loading={promoteBuildToTestingTrackLoading}
                        overlay={testTrackOptionsMenu}
                        disabled={props.betaStatus === BetaStatus.Testing}
                    >
                        Promote to {defaultTrack} track
                    </PermissionedActionButton>
                )}
                {props.betaStatus === BetaStatus.Testing && (
                    <Button
                        type="link"
                        icon={<ArrowRightOutlined />}
                        target={'_blank'}
                        href={PLAY_CONSOLE_URL}
                        size="small"
                    >
                        View in Play Console
                    </Button>
                )}
            </Space>
            <PromoteBuildConfirmationModal
                isVisible={!!pendingPromotionTrack}
                isLoading={promoteBuildToTestingTrackLoading}
                appStoreBuilds={[props.appStoreBuild]}
                trackName={pendingPromotionTrack ?? defaultTrack}
                onConfirm={confirmPromotion}
                onCancel={cancelPromotion}
            />
        </>
    );
};

interface BetaTestingHistoryProps extends ReleaseComponentProps {
    betaBuilds: BetaBuild[];
    testTracks: TestTrack[];
    betaGroups: BetaTestingGroup[];
    betaTesters: BetaTester[];
    betaIntegrationType: IntegrationType;
    defaultTestTrack: string;
    stepRunComponentProps: Nullable<StepRunComponentProps>;
    autoAssignBetaGroupsEnabled: boolean;
    autoApplyBetaBuildNotesAutomationIsActive: boolean;
}

const BetaTestingHistory: React.FC<BetaTestingHistoryProps> = (props: BetaTestingHistoryProps): JSX.Element => {
    const [selectedExternalBuildsToPromote, setSelectedExternalBuildsToPromote] = React.useState({});
    const [pendingMultiPromotionTrack, setPendingMultiPromotionTrack] = React.useState<string | null>(null);
    const dispatch = useDispatch();

    const defaultTestTrack = props.defaultTestTrack;

    const betaIntegrationLinkUrl = useSelector((state: RootState) => {
        return selectBetaIntegrationLinkUrl(state, props.appId, props.betaIntegrationType);
    });

    const [
        promoteBuildsToTestingTrackStepRun,
        promoteBuildsToTestingTrackIsLoadingStepRun,
        promoteBuildsToTestingTrackResponseTypeStepRun,
        promoteBuildsToTestingTrackErrorStepRun,
    ] = useCallUpdatable(
        StepRunUpdatable.PromoteBuildsToTestingTrack,
        props.stepRunComponentProps,
        props.stepRunComponentProps?.releaseId,
    );

    const promoteBuildsToTestingTrackLoadingReleaseStep = useIsLoading(
        ReleaseUpdatable.PromoteBuildToTestingTrack,
        props.releaseId,
    );
    const promoteBuildsToTestingTrackSuccessReleaseStep = useUpdated(
        ReleaseUpdatable.PromoteBuildToTestingTrack,
        props.releaseId,
    );
    const promoteBuildsToTestingTrackErrorReleaseStep = useError(
        ReleaseUpdatable.PromoteBuildToTestingTrack,
        props.releaseId,
    );

    const promoteBuildsToTestingTrackLoading =
        props.stepRunComponentProps ?
            promoteBuildsToTestingTrackIsLoadingStepRun
        :   promoteBuildsToTestingTrackLoadingReleaseStep;
    const promoteBuildsToTestingTrackSuccess =
        props.stepRunComponentProps ?
            promoteBuildsToTestingTrackResponseTypeStepRun === RunwayResponseType.Success
        :   promoteBuildsToTestingTrackSuccessReleaseStep;
    const promoteBuildsToTestingTrackError =
        props.stepRunComponentProps ?
            promoteBuildsToTestingTrackErrorStepRun
        :   promoteBuildsToTestingTrackErrorReleaseStep;

    const allowMultiSelect = isGoogleBetaIntegration(props.betaIntegrationType);

    const currentlyTestingBuildIds = props.betaBuilds
        .filter((build) => build.betaStatus === BetaStatus.Testing)
        .map((build) => parseInt(build.appStoreBuild.appStoreBuild.buildNumber))
        .sort();
    const highestTestingBuildId = currentlyTestingBuildIds.length > 0 ? currentlyTestingBuildIds[0] : null;

    const promoteableTestTracks = props.testTracks.filter((track) => track.name !== 'internal');

    const executeMultipleBuildsPromotion = (buildIds: string[], track: string) => {
        const request: PromoteToTestingTrackRequestReleaseStep = {
            orgId: props.orgId,
            buildIds: buildIds,
            appId: props.appId,
            releaseId: props.releaseId,
            track,
        };

        if (props.stepRunComponentProps) {
            promoteBuildsToTestingTrackStepRun(request);
        } else {
            dispatch(tryUpdateRelease(ReleaseUpdatable.PromoteBuildToTestingTrack, request, props.releaseId));
        }
    };

    const getSelectedBuildIds = () => {
        return reduce(
            selectedExternalBuildsToPromote,
            (selectedBuilds, isSelected, key) => {
                if (isSelected) {
                    selectedBuilds.push(key);
                    return selectedBuilds;
                } else {
                    return selectedBuilds;
                }
            },
            [],
        );
    };

    const getSelectedBuilds = () => {
        const selectedIds = getSelectedBuildIds();
        return props.betaBuilds
            .filter((build) => selectedIds.includes(build.appStoreBuild.appStoreBuild.id))
            .map((build) => build.appStoreBuild);
    };

    const showMultiPromotionModal = (track: string = null) => {
        setPendingMultiPromotionTrack(track ?? defaultTestTrack);
    };

    const confirmMultiPromotion = () => {
        const selectedBuildIds = getSelectedBuildIds();
        executeMultipleBuildsPromotion(
            selectedBuildIds,
            pendingMultiPromotionTrack !== defaultTestTrack ? pendingMultiPromotionTrack : null,
        );
        setPendingMultiPromotionTrack(null);
    };

    const cancelMultiPromotion = () => {
        setPendingMultiPromotionTrack(null);
    };

    const genExtra = (build: BetaBuild) => {
        let activeReasonText = null;

        switch (build.selectedReason) {
            case CIBuildSelectionReason.ManualSelection:
                activeReasonText = 'Active (manual selection)';
                break;
            case CIBuildSelectionReason.AutoSelection:
                activeReasonText = 'Active (latest build)';
                break;
        }

        const statusColor = colorForBetaBuildStatus(build.betaStatus);
        const displayText = displayTextForBetaBuildStatus(build.betaStatus);

        return (
            <div style={{ display: 'inline' }} onClick={(e) => e.stopPropagation()}>
                <Space align="center">
                    {build.isSelectedBuild && (
                        <Text style={{ color: runwayColors.statusGreen, fontWeight: 600, whiteSpace: 'nowrap' }}>
                            {activeReasonText}
                        </Text>
                    )}
                    <Tag
                        color={runwayColors.white}
                        style={{ ...tagStyle, fontSize: 10, borderColor: statusColor, color: statusColor }}
                    >
                        {displayText}
                        {build.testTrack ? ` (${build.testTrack})` : ''}
                    </Tag>
                    {isGoogleBetaIntegration(build.appStoreBuild.appStoreBuild.integrationId) && (
                        <GoogleBetaBuildCtas
                            {...build}
                            testTracks={promoteableTestTracks}
                            orgId={props.orgId}
                            appId={props.appId}
                            releaseId={props.releaseId}
                            defaultTestTrack={defaultTestTrack}
                            highestTestingBuildId={highestTestingBuildId}
                            stepRunComponentProps={props.stepRunComponentProps}
                        />
                    )}
                    {isTestFlightBetaIntegration(build.appStoreBuild.appStoreBuild.integrationId) && (
                        <Button
                            type="link"
                            icon={<ArrowRightOutlined />}
                            target={'_blank'}
                            href={`${betaIntegrationLinkUrl}/ios/${build.appStoreBuild.appStoreBuild.id}`}
                            size="small"
                        >
                            View in TestFlight
                        </Button>
                    )}
                </Space>
            </div>
        );
    };

    const renderItem = (build: BetaBuild, index: number) => {
        const checkboxSelected = selectedExternalBuildsToPromote[build.appStoreBuild.appStoreBuild.id];
        // TODO: Full implementation should be handled in this ticket https://linear.app/runway-org/issue/RUN-11810
        const canPromoteBuild = true;
        // const canPromoteBuild =
        //     highestTestingBuildId ?
        //         parseInt(build.appStoreBuild.appStoreBuild.buildNumber) > highestTestingBuildId
        //     :   true;

        const promoteBuildsCheckbox = () => {
            return (
                <Space align="center" size={16}>
                    <div onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                            checked={checkboxSelected}
                            onChange={(e) => {
                                const updatedSelectedExternalBuildsToPromote = {
                                    ...selectedExternalBuildsToPromote,
                                    [build.appStoreBuild.appStoreBuild.id]: e.target.checked,
                                };
                                setSelectedExternalBuildsToPromote(updatedSelectedExternalBuildsToPromote);
                            }}
                            disabled={build.betaStatus === BetaStatus.Testing || !canPromoteBuild}
                        />
                    </div>
                </Space>
            );
        };

        return (
            <List.Item key={index}>
                <Collapse ghost bordered={false}>
                    <Collapse.Panel
                        className="collapse-panel-rc-builds"
                        key={'build-' + index}
                        header={
                            <BetaBuildHistoryItemHeader
                                checkbox={allowMultiSelect ? promoteBuildsCheckbox() : null}
                                appId={props.appId}
                                {...build}
                            />
                        }
                        extra={genExtra(build)}
                    >
                        <Space direction="vertical" style={{ width: '100%' }} size={20}>
                            <BetaWhatToTestModule
                                stepRunComponentProps={props.stepRunComponentProps}
                                orgId={props.orgId}
                                appId={props.appId}
                                releaseId={props.releaseId}
                                externalBuildId={build.appStoreBuild.appStoreBuild.id}
                                betaNotes={build.betaTestingNotes ?? []}
                                betaIntegrationType={build.appStoreBuild.appStoreBuild.integrationId}
                                editTestInformationDisabled={
                                    isGoogleBetaIntegration(props.betaIntegrationType) &&
                                    build.betaStatus === BetaStatus.NotTesting
                                }
                                autoApplyBetaBuildNotesAutomationIsActive={
                                    props.autoApplyBetaBuildNotesAutomationIsActive
                                }
                            />
                            <BetaGroupsModule
                                stepRunComponentProps={props.stepRunComponentProps}
                                orgId={props.orgId}
                                allTesters={props.betaTesters}
                                allGroups={props.betaGroups}
                                selectedTesters={build.betaTesters ?? []}
                                selectedGroups={build.betaGroups ?? []}
                                appId={props.appId}
                                releaseId={props.releaseId}
                                externalBuildId={build.appStoreBuild.appStoreBuild.id}
                                betaIntegrationType={build.appStoreBuild.appStoreBuild.integrationId}
                                editGroupsDisabled={false}
                                autoAssignBetaGroupsEnabled={props.autoAssignBetaGroupsEnabled}
                            />
                            {getStabilityInfos(build.appStoreBuild).length > 0 && (
                                <div
                                    style={{
                                        ...releaseStepDetailsContainerModuleStyle,
                                        backgroundColor: antGrey.grey2,
                                    }}
                                >
                                    <StabilityMetricsContainer
                                        appId={props.appId}
                                        backgroundColor={runwayColors.white}
                                        isRunwayAutoPaused={false}
                                        builds={[build.appStoreBuild]}
                                    />
                                </div>
                            )}
                        </Space>
                    </Collapse.Panel>
                </Collapse>
            </List.Item>
        );
    };

    const listHeader = () => {
        const selectedBuildIdsToPromote = allowMultiSelect ? getSelectedBuildIds() : [];

        const promoteMultipleOverlay = promoteableTestTracks.length > 0 && (
            <Menu
                onClick={(item) => {
                    if (item.key) {
                        showMultiPromotionModal(item.key);
                    }
                }}
                items={promoteableTestTracks
                    .map((testTrack) => ({
                        label: `Promote multiple to ${testTrack.name} track`,
                        key: testTrack.name,
                    }))
                    .filter((item) => item.key !== defaultTestTrack)}
            />
        );

        return (
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text style={{ fontSize: 20, color: antGrey.grey7 }}>
                    Build history – {props.betaBuilds.length} {props.betaBuilds.length === 1 ? 'build' : 'builds'}
                </Text>
                {allowMultiSelect && selectedBuildIdsToPromote.length > 0 && (
                    <PermissionedActionButton
                        type="primary"
                        releaseId={props.releaseId}
                        actionType={RunwayUserAction.UAPromoteBetaBuildToTestingTrack}
                        onClick={() => showMultiPromotionModal()}
                        loading={promoteBuildsToTestingTrackLoading}
                        overlay={promoteMultipleOverlay}
                    >
                        Promote multiple to {defaultTestTrack} track
                    </PermissionedActionButton>
                )}
            </div>
        );
    };

    React.useEffect(() => {
        if (promoteBuildsToTestingTrackSuccess) {
            globalMessage.success('Builds promoted to testing track!');

            // clear selected builds and modal
            setSelectedExternalBuildsToPromote({});
            setPendingMultiPromotionTrack(null);
        }
    }, [promoteBuildsToTestingTrackSuccess]);

    React.useEffect(() => {
        if (promoteBuildsToTestingTrackError) {
            globalMessage.error(promoteBuildsToTestingTrackError);
        }
    }, [promoteBuildsToTestingTrackError]);

    return (
        <div style={{ width: '100%', marginTop: 24 }}>
            <List
                className="build-history-list"
                header={listHeader()}
                locale={{ emptyText: 'No builds found.' }}
                dataSource={props.betaBuilds}
                itemLayout="vertical"
                pagination={{ defaultPageSize: 10, hideOnSinglePage: true }}
                renderItem={renderItem}
                size="small"
            />
            <PromoteBuildConfirmationModal
                isVisible={!!pendingMultiPromotionTrack}
                isLoading={promoteBuildsToTestingTrackLoading}
                appStoreBuilds={getSelectedBuilds()}
                trackName={pendingMultiPromotionTrack ?? defaultTestTrack}
                onConfirm={confirmMultiPromotion}
                onCancel={cancelMultiPromotion}
            />
        </div>
    );
};

export default BetaTestingHistory;
